import React from 'react';
import { Chip, Grid, IconButton, TextField } from '@material-ui/core';
import { Add } from '@material-ui/icons';
import { FieldArray, useFormikContext } from 'formik';
import { DatePicker } from '@material-ui/pickers';
import AsyncSearch from 'components/AsyncSearch';
import { getEquipmentListByEquipmentType } from 'pages/PageEquipmentPprList/api';
import { IFormValues } from './BaseJournalEquipmentItemDialog';

interface IProps {
  disabled?: boolean;
  equipmentTypeId?: string;
}

const JournalEquipmentItemForm: React.FC<IProps> = ({ disabled = false, equipmentTypeId }) => {
  const { values, setFieldValue, touched, errors, handleChange, handleBlur } = useFormikContext<IFormValues>();

  return (
    <Grid container spacing={2}>
      <Grid item lg={6} md={6} xs={12}>
        <AsyncSearch
          fetch={() => getEquipmentListByEquipmentType(equipmentTypeId!)}
          uniqueBy="equipmentId"
          matchKeys={['equipmentName']}
          value={values.equipment}
          onChange={(_, option) => setFieldValue('equipment', option)}
          disabled={disabled}
          getOptionLabel={(option) => String(option.equipmentName)}
          getOptionSelected={(option, value) => option?.equipmentId === value?.equipmentId}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Оборудование"
              variant="filled"
              fullWidth
              error={touched.equipment && Boolean(errors.equipment)}
              helperText={touched.equipment && errors.equipment}
            />
          )}
        />
      </Grid>

      <Grid item lg={6} md={6} xs={12}>
        <TextField
          name="equipmentCount"
          type="number"
          variant="filled"
          fullWidth
          label="Количество"
          disabled={disabled}
          value={values.equipmentCount}
          onChange={handleChange}
          onBlur={handleBlur}
          error={touched.equipmentCount && Boolean(errors.equipmentCount)}
          helperText={touched.equipmentCount && errors.equipmentCount}
        />
      </Grid>

      <Grid item lg={6} md={6} xs={12}>
        <TextField
          name="equipmentCountry"
          variant="filled"
          fullWidth
          label="Производитель"
          disabled={disabled}
          value={values.equipmentCountry}
          onChange={handleChange}
          onBlur={handleBlur}
          error={touched.equipmentCountry && Boolean(errors.equipmentCountry)}
          helperText={touched.equipmentCountry && errors.equipmentCountry}
        />
      </Grid>

      <Grid item lg={6} md={6} xs={12}>
        <TextField
          name="equipmentModel"
          variant="filled"
          fullWidth
          label="Модель"
          disabled={disabled}
          value={values.equipmentModel}
          onChange={handleChange}
          onBlur={handleBlur}
          error={touched.equipmentModel && Boolean(errors.equipmentModel)}
          helperText={touched.equipmentModel && errors.equipmentModel}
        />
      </Grid>

      <FieldArray name="equipmentSerialNumber">
        {({ push, remove }) => (
          <>
            <Grid item lg={7} md={7} xs={12}>
              <TextField
                name="equipmentSerialNumberItem"
                variant="filled"
                fullWidth
                label="Серийный номер"
                disabled={disabled}
                value={values.equipmentSerialNumberItem}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.equipmentSerialNumberItem && Boolean(errors.equipmentSerialNumberItem)}
                helperText={touched.equipmentSerialNumberItem && errors.equipmentSerialNumberItem}
                InputProps={{
                  endAdornment: (
                    <IconButton
                      disabled={disabled}
                      onClick={() => {
                        if (!values.equipmentSerialNumberItem) return;

                        push(values.equipmentSerialNumberItem);
                        setFieldValue('equipmentSerialNumberItem', '');
                      }}
                    >
                      <Add />
                    </IconButton>
                  ),
                }}
              />
            </Grid>

            {Boolean(values.equipmentSerialNumber?.length) && (
              <Grid item xs={12}>
                <Grid container spacing={1}>
                  {values.equipmentSerialNumber.map((serialNumber: string, index: number) => (
                    <Grid item key={serialNumber + index}>
                      <Chip label={serialNumber} disabled={disabled} onDelete={() => remove(index)} />
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            )}
          </>
        )}
      </FieldArray>

      <Grid item lg={6} md={6} xs={12}>
        <TextField
          type="number"
          name="equipmentCost"
          variant="filled"
          fullWidth
          label="Стоимость"
          disabled={disabled}
          value={values.equipmentCost}
          onChange={handleChange}
          onBlur={handleBlur}
          error={touched.equipmentCost && Boolean(errors.equipmentCost)}
          helperText={touched.equipmentCost && errors.equipmentCost}
        />
      </Grid>

      <Grid item lg={6} md={6} xs={12}>
        <DatePicker
          inputVariant="filled"
          format="dd.MM.yyyy"
          fullWidth
          label="Дата начала эксплуатации"
          disabled={disabled}
          value={values.dateStart}
          onChange={(date) => setFieldValue('dateStart', date)}
          error={touched.dateStart && Boolean(errors.dateStart)}
          helperText={touched.dateStart && errors.dateStart}
        />
      </Grid>

      <Grid item lg={6} md={6} xs={12}>
        <TextField
          name="monthCount"
          type="number"
          variant="filled"
          fullWidth
          label="Срок эксплуатации (месяцы)"
          disabled={disabled}
          value={values.monthCount}
          onChange={handleChange}
          onBlur={handleBlur}
          error={touched.monthCount && Boolean(errors.monthCount)}
          helperText={touched.monthCount && errors.monthCount}
        />
      </Grid>

      <Grid item lg={6} md={6} xs={12}>
        <TextField
          multiline
          name="comment"
          variant="filled"
          fullWidth
          label="Комментарий"
          disabled={disabled}
          value={values.comment}
          onChange={handleChange}
          onBlur={handleBlur}
          error={touched.comment && Boolean(errors.comment)}
          helperText={touched.comment && errors.comment}
        />
      </Grid>

      <Grid item lg={6} md={6} xs={12}>
        <TextField
          name="guaranteeMonth"
          type="number"
          variant="filled"
          fullWidth
          label="Гарантийный срок (месяцы)"
          disabled={disabled}
          value={values.guaranteeMonth}
          onChange={handleChange}
          onBlur={handleBlur}
          error={touched.guaranteeMonth && Boolean(errors.guaranteeMonth)}
          helperText={touched.guaranteeMonth && errors.guaranteeMonth}
        />
      </Grid>
    </Grid>
  );
};

export default JournalEquipmentItemForm;
